/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import { IAdminDailyMetricData } from '@datatype/admin/api-type';
import Spinner from '@design-system/spinner';
import DailyItem from '@component/admin/student/student-home/daily-list/daily-item';

interface IProps {
  dailies: IAdminDailyMetricData[];
  selectedDate: string;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  handleSelect: (date: string) => void;
  handleRetry: () => void;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
}

const List = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

const NextLoader = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
`;

const Empty = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${color.BLACK_5};
`;

const Sentinel = styled.div`
  height: 1px;
`;

const DailyList: React.FC<IProps> = ({
  dailies,
  selectedDate,
  isLoading,
  isFetchingNextPage,
  isError,
  handleSelect,
  handleRetry,
  sentinelRef,
}) => {
  if (isLoading) {
    return (
      <Loader>
        <Spinner size='32px' color={color.BROWN_1} />
      </Loader>
    );
  }
  if (isError) {
    return (
      <Empty>
        데이터를 불러오지 못했습니다.{' '}
        <span
          style={{ color: color.BROWN_1, cursor: 'pointer', textDecoration: 'underline' }}
          onClick={handleRetry}>
          다시 시도
        </span>
      </Empty>
    );
  }
  if (dailies.length === 0) {
    return <Empty>데이터가 없습니다.</Empty>;
  }
  return (
    <>
      <List>
        {dailies.map((d) => (
          <DailyItem
            key={d.date}
            daily={d}
            isSelected={d.date === selectedDate}
            handleClick={() => handleSelect(d.date)}
          />
        ))}
      </List>
      {isFetchingNextPage && (
        <NextLoader>
          <Spinner size='24px' color={color.BROWN_1} />
        </NextLoader>
      )}
      <Sentinel ref={sentinelRef} />
    </>
  );
};

export default DailyList;